import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getAuditPrLog } from '../lib/api';
import type {
  AuditEntry,
  AuditOutcome,
  AuditBugClass,
  AuditResponsibleElement,
  AuditLogResponse,
} from '../types';
import styles from './AuditPage.module.css';

// ─── Constants ──────────────────────────────────────────────────────────────

const OUTCOME_META: Record<AuditOutcome, { label: string; color: string }> = {
  clean_merge:      { label: 'Clean merge',      color: 'var(--success)' },
  rework_and_merge: { label: 'Rework & merge',   color: 'var(--warning)' },
  closed_unmerged:  { label: 'Closed unmerged',  color: 'var(--muted)' },
  reverted:         { label: 'Reverted',         color: 'var(--danger)' },
};
const OUTCOME_ORDER: AuditOutcome[] = ['clean_merge', 'rework_and_merge', 'closed_unmerged', 'reverted'];

const RESPONSIBLE_META: Record<AuditResponsibleElement, { label: string; color: string }> = {
  pame:             { label: 'Pame',           color: 'var(--broota)' },
  neo:              { label: 'Neo',            color: 'var(--xbroota)' },
  kika:             { label: 'Kika',           color: 'var(--nalca)' },
  'skill:jab-tdd':  { label: 'Skill JAB-TDD',  color: 'var(--accent)' },
  infra:            { label: 'Infra',          color: 'var(--warning)' },
  humano:           { label: 'Humano',         color: 'var(--dully)' },
};
const RESPONSIBLE_ORDER: AuditResponsibleElement[] = ['pame', 'neo', 'kika', 'skill:jab-tdd', 'infra', 'humano'];

const BUG_CLASS_LABEL: Record<AuditBugClass, string> = {
  missing_dependency:       'Missing dependency',
  orphan_import:            'Orphan import',
  stale_dependency:         'Stale dependency',
  partial_file_read:        'Partial file read',
  mock_driven_development:  'Mock-driven dev',
  test_never_ran:           'Test never ran',
  ssr_unsafe:               'SSR unsafe',
  type_escape_hatch:        'Type escape hatch',
  markdown_destruction:     'Markdown destruction',
  spec_drift:               'Spec drift',
  context_incompleto_pame:  'Context incompleto (Pame)',
  otro:                     'Otro',
};

type RangeKey = '7d' | '30d' | '90d' | 'all';
const RANGES: { key: RangeKey; label: string }[] = [
  { key: '7d',  label: '7D'  },
  { key: '30d', label: '30D' },
  { key: '90d', label: '90D' },
  { key: 'all', label: 'ALL' },
];

const AUTO_REFRESH_MS = 60_000;
const STALE_TICK_MS   = 15_000;

function sinceIsoFor(range: RangeKey): string | undefined {
  if (range === 'all') return undefined;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

function formatAgo(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = Date.now() - Date.parse(iso);
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `hace ${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `hace ${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h}h`;
  const d = Math.round(h / 24);
  return `hace ${d}d`;
}

// ─── Horizontal bar list (responsible_elements, bug_classes) ────────────────

function BarList<K extends string>({
  data, order, maxEntries, getLabel, getColor,
  activeKey, onToggle,
}: {
  data: Partial<Record<K, number>>;
  order?: K[];
  maxEntries?: number;
  getLabel: (k: K) => string;
  getColor: (k: K) => string;
  activeKey?: K | null;
  onToggle?: (k: K) => void;
}) {
  const entries = Object.entries(data) as [K, number][];
  if (!entries.length) {
    return <div className={styles.emptyState}>Sin datos en este rango.</div>;
  }
  const ordered = order
    ? order.filter(k => data[k]).map<[K, number]>(k => [k, data[k] as number])
    : [...entries].sort((a, b) => b[1] - a[1]);

  const sliced = maxEntries ? ordered.slice(0, maxEntries) : ordered;
  const max    = Math.max(...sliced.map(([, n]) => n), 1);

  return (
    <div className={styles.barList}>
      {sliced.map(([k, count]) => {
        const pct = (count / max) * 100;
        const isActive = activeKey === k;
        const clickable = typeof onToggle === 'function';
        return (
          <div
            key={k}
            className={styles.barRow}
            role={clickable ? 'button' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={clickable ? () => onToggle!(k) : undefined}
            onKeyDown={clickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle!(k); }
            } : undefined}
            style={clickable ? { cursor: 'pointer' } : undefined}
            aria-pressed={clickable ? isActive : undefined}
            title={clickable ? `Filtrar tabla por ${getLabel(k)}` : undefined}
          >
            <span className={styles.barLabel}>{getLabel(k)}</span>
            <div className={styles.barTrack} aria-label={`${getLabel(k)}: ${count}`}>
              <div
                className={styles.barFill}
                style={{
                  width: `${pct}%`,
                  background: getColor(k),
                  opacity: isActive ? 1 : 0.85,
                  boxShadow: isActive ? '0 0 0 1px var(--accent) inset' : 'none',
                }}
              />
            </div>
            <span className={styles.barCount}>{count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Stacked horizontal bar for outcome distribution ────────────────────────

function OutcomeStacked({ data }: { data: Partial<Record<AuditOutcome, number>> }) {
  const total = OUTCOME_ORDER.reduce((s, k) => s + (data[k] ?? 0), 0);
  if (total === 0) {
    return <div className={styles.emptyState}>Sin datos en este rango.</div>;
  }
  return (
    <>
      <div className={styles.stackedBar} role="img" aria-label="Distribución de outcomes">
        {OUTCOME_ORDER.map(k => {
          const n = data[k] ?? 0;
          if (n === 0) return null;
          const pct = (n / total) * 100;
          return (
            <div
              key={k}
              className={styles.stackedSeg}
              style={{ width: `${pct}%`, background: OUTCOME_META[k].color }}
              title={`${OUTCOME_META[k].label}: ${n} (${pct.toFixed(0)}%)`}
            />
          );
        })}
      </div>
      <div className={styles.legend}>
        {OUTCOME_ORDER.map(k => {
          const n = data[k] ?? 0;
          return (
            <span key={k} className={styles.legendItem}>
              <span className={styles.legendDot} style={{ background: OUTCOME_META[k].color }} />
              {OUTCOME_META[k].label}
              <span className={styles.legendCount}>{n}</span>
            </span>
          );
        })}
      </div>
    </>
  );
}

// ─── Donut for avg iterations + clean merge ratio ──────────────────────────

function CleanMergeDonut({
  outcome, avgIterations,
}: {
  outcome: Partial<Record<AuditOutcome, number>>;
  avgIterations: number | null;
}) {
  const total = OUTCOME_ORDER.reduce((s, k) => s + (outcome[k] ?? 0), 0);
  const clean = outcome.clean_merge ?? 0;
  const pct   = total > 0 ? (clean / total) * 100 : 0;
  const radius = 60;
  const stroke = 14;
  const circ  = 2 * Math.PI * radius;
  const dash  = (pct / 100) * circ;

  return (
    <div className={styles.donutWrap}>
      <svg
        className={styles.donutSvg}
        viewBox="0 0 160 160"
        role="img"
        aria-label={`Clean merge: ${pct.toFixed(0)}%. Avg iteraciones: ${avgIterations ?? 'n/d'}`}
      >
        <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--surface2)" strokeWidth={stroke} />
        <circle
          cx="80" cy="80" r={radius} fill="none"
          stroke="var(--success)"
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeDashoffset={circ / 4}
          strokeLinecap="round"
          transform="rotate(-90 80 80)"
          style={{ transition: 'stroke-dasharray 400ms ease' }}
        />
        <text
          x="80" y="72" textAnchor="middle"
          className={styles.donutCenterValue}
          fontSize="22"
        >
          {total > 0 ? `${pct.toFixed(0)}%` : '—'}
        </text>
        <text
          x="80" y="92" textAnchor="middle"
          className={styles.donutCenterLabel}
          fontSize="10"
          letterSpacing="0.8"
        >
          CLEAN MERGE
        </text>
        <text
          x="80" y="110" textAnchor="middle"
          className={styles.donutCenterLabel}
          fontSize="9"
        >
          {avgIterations != null ? `avg ${avgIterations} iters` : ''}
        </text>
      </svg>
    </div>
  );
}

// ─── Trend sparkline (clean-merge % over time) ─────────────────────────────

function TrendSparkline({ entries, range }: { entries: AuditEntry[]; range: RangeKey }) {
  const { values, buckets } = useMemo(() => {
    const bucketMs = range === '7d' ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
    const numBuckets = range === '7d' ? 7 : range === '30d' ? 5 : range === '90d' ? 13 : 13;
    const now = Date.now();
    const start = now - numBuckets * bucketMs;
    const bs = Array.from({ length: numBuckets }, (_, i) => ({
      total: 0,
      clean: 0,
      endMs: start + (i + 1) * bucketMs,
    }));
    for (const e of entries) {
      const t = Date.parse(e.timestamp);
      if (Number.isNaN(t) || t < start || t > now) continue;
      const idx = Math.min(numBuckets - 1, Math.max(0, Math.floor((t - start) / bucketMs)));
      bs[idx].total += 1;
      if (e.outcome === 'clean_merge') bs[idx].clean += 1;
    }
    const vals: (number | null)[] = bs.map(b => (b.total > 0 ? (b.clean / b.total) * 100 : null));
    return { values: vals, buckets: bs };
  }, [entries, range]);

  const hasData = values.some(v => v !== null);
  if (!hasData) {
    return <div className={styles.emptyState}>Sin datos suficientes para trend.</div>;
  }

  const w = 600, h = 80, padY = 6;
  const step = values.length > 1 ? w / (values.length - 1) : 0;
  const points = values.map((v, i) => ({
    x: i * step,
    y: v == null ? null : h - ((v / 100) * (h - padY * 2)) - padY,
  }));
  const valid = points.filter(p => p.y !== null) as { x: number; y: number }[];
  const path  = valid.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const area  = valid.length >= 2
    ? `${path} L${valid[valid.length - 1].x.toFixed(1)} ${h} L${valid[0].x.toFixed(1)} ${h} Z`
    : '';

  const firstVal = values.find(v => v !== null) ?? null;
  const lastVal  = [...values].reverse().find(v => v !== null) ?? null;
  const delta    = (firstVal != null && lastVal != null) ? lastVal - firstVal : null;
  const bucketLabel = range === '7d' ? 'día' : 'sem';
  const totalPrs = buckets.reduce((s, b) => s + b.total, 0);

  return (
    <div className={styles.sparkWrap}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        className={styles.sparkSvg}
        role="img"
        aria-label={`Clean merge % por ${bucketLabel}`}
      >
        {/* 50% guideline */}
        <line
          x1="0" x2={w} y1={h / 2} y2={h / 2}
          stroke="var(--border)" strokeWidth="1" strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
        {area && <path d={area} fill="var(--success)" opacity="0.14" />}
        {path && (
          <path
            d={path}
            stroke="var(--success)"
            strokeWidth="1.6"
            fill="none"
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {valid.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r="2"
            fill="var(--success)"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <div className={styles.sparkLegend}>
        <span>
          {buckets.length} {bucketLabel}{buckets.length !== 1 ? 's' : ''} · {totalPrs} PRs
        </span>
        <span>
          {firstVal != null ? `${firstVal.toFixed(0)}%` : '—'}
          {' → '}
          {lastVal != null ? `${lastVal.toFixed(0)}%` : '—'}
          {delta != null && (
            <span
              className={styles.sparkDelta}
              style={{
                color: delta > 0 ? 'var(--success)' : delta < 0 ? 'var(--danger)' : 'var(--muted)',
              }}
            >
              {delta > 0 ? '▲' : delta < 0 ? '▼' : '='}
              {Math.abs(delta).toFixed(0)}pp
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

// ─── Breakdown per repo (stacked mini-bars) ─────────────────────────────────

function RepoBreakdown({ entries }: { entries: AuditEntry[] }) {
  const byRepo = useMemo(() => {
    const m = new Map<string, Partial<Record<AuditOutcome, number>>>();
    for (const e of entries) {
      const cur = m.get(e.repo) ?? {};
      cur[e.outcome] = (cur[e.outcome] ?? 0) + 1;
      m.set(e.repo, cur);
    }
    return Array.from(m.entries())
      .map(([repo, outcome]) => {
        const total = OUTCOME_ORDER.reduce((s, k) => s + (outcome[k] ?? 0), 0);
        const clean = outcome.clean_merge ?? 0;
        return { repo, outcome, total, cleanPct: total > 0 ? (clean / total) * 100 : 0 };
      })
      .sort((a, b) => b.total - a.total);
  }, [entries]);

  if (!byRepo.length) {
    return <div className={styles.emptyState}>Sin repos en este rango.</div>;
  }

  return (
    <div className={styles.repoList}>
      {byRepo.map(({ repo, outcome, total, cleanPct }) => (
        <div key={repo} className={styles.repoRow}>
          <span className={styles.repoName} title={repo}>{repo}</span>
          <div className={styles.repoBar} aria-label={`${repo}: ${total} PRs`}>
            {OUTCOME_ORDER.map(k => {
              const n = outcome[k] ?? 0;
              if (n === 0) return null;
              const pct = (n / total) * 100;
              return (
                <div
                  key={k}
                  className={styles.repoSeg}
                  style={{ width: `${pct}%`, background: OUTCOME_META[k].color }}
                  title={`${OUTCOME_META[k].label}: ${n} (${pct.toFixed(0)}%)`}
                />
              );
            })}
          </div>
          <span className={styles.repoCleanPct} title="Clean merge %">
            {cleanPct.toFixed(0)}%
          </span>
          <span className={styles.repoCount}>{total}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Heatmap bug_class × responsible_element ────────────────────────────────

function BugHeatmap({ entries }: { entries: AuditEntry[] }) {
  const { matrix, rows, cols, max } = useMemo(() => {
    const m: Partial<Record<AuditBugClass, Partial<Record<AuditResponsibleElement, number>>>> = {};
    const bugSet  = new Set<AuditBugClass>();
    const respSet = new Set<AuditResponsibleElement>();
    for (const e of entries) {
      if (!e.bug_classes.length || !e.responsible_elements.length) continue;
      for (const b of e.bug_classes) {
        bugSet.add(b);
        if (!m[b]) m[b] = {};
        for (const r of e.responsible_elements) {
          respSet.add(r);
          m[b]![r] = (m[b]![r] ?? 0) + 1;
        }
      }
    }
    const rows = Array.from(bugSet).sort((a, b) => {
      const ta = Object.values(m[a] ?? {}).reduce((s, n) => s + (n ?? 0), 0);
      const tb = Object.values(m[b] ?? {}).reduce((s, n) => s + (n ?? 0), 0);
      return tb - ta;
    });
    const cols = RESPONSIBLE_ORDER.filter(r => respSet.has(r));
    let max = 0;
    for (const b of rows) for (const r of cols) {
      const v = m[b]?.[r] ?? 0;
      if (v > max) max = v;
    }
    return { matrix: m, rows, cols, max };
  }, [entries]);

  if (!rows.length || !cols.length) {
    return <div className={styles.emptyState}>Sin cruces bug × responsable.</div>;
  }

  return (
    <div className={styles.heatmapWrap}>
      <table className={styles.heatmap}>
        <thead>
          <tr>
            <th className={styles.heatCorner} aria-hidden="true" />
            {cols.map(c => (
              <th key={c} scope="col" className={styles.heatColHeader}>
                <span className={styles.heatColLabel}>
                  {RESPONSIBLE_META[c]?.label ?? c}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(b => (
            <tr key={b}>
              <th scope="row" className={styles.heatRowHeader}>
                {BUG_CLASS_LABEL[b] ?? b}
              </th>
              {cols.map(c => {
                const n = matrix[b]?.[c] ?? 0;
                const intensity = max > 0 ? n / max : 0;
                const color = RESPONSIBLE_META[c]?.color ?? 'var(--accent)';
                const pct = Math.round(8 + intensity * 72);
                return (
                  <td
                    key={c}
                    className={styles.heatCell}
                    style={{
                      background: n > 0
                        ? `color-mix(in srgb, ${color} ${pct}%, transparent)`
                        : 'transparent',
                      color: intensity > 0.55 ? 'var(--text)' : 'var(--muted)',
                    }}
                    title={`${BUG_CLASS_LABEL[b] ?? b} × ${RESPONSIBLE_META[c]?.label ?? c}: ${n}`}
                  >
                    {n > 0 ? n : ''}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function AuditPage() {
  const { teamUser, loading } = useAuth();
  const [range, setRange]             = useState<RangeKey>('all');
  const [bugFilter, setBugFilter]     = useState<AuditBugClass | null>(null);
  const [data, setData]               = useState<AuditLogResponse | null>(null);
  const [fetching, setFetching]       = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [, setStaleTick]              = useState(0);
  const lastRefreshRef                = useRef<number>(Date.now());

  // Fetch when range changes or refresh is triggered
  useEffect(() => {
    let cancelled = false;
    setFetching(true);
    setError(null);
    getAuditPrLog({ limit: 200, since: sinceIsoFor(range) })
      .then(res => { if (!cancelled) { setData(res); lastRefreshRef.current = Date.now(); } })
      .catch((e: Error) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setFetching(false); });
    return () => { cancelled = true; };
  }, [range, refreshTick]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => setRefreshTick(t => t + 1), AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh]);

  // Tick for staleness indicator (no re-fetch, just re-render)
  useEffect(() => {
    const id = setInterval(() => setStaleTick(t => t + 1), STALE_TICK_MS);
    return () => clearInterval(id);
  }, []);

  const filteredEntries = useMemo(() => {
    if (!data) return [];
    if (!bugFilter) return data.entries;
    return data.entries.filter(e => e.bug_classes.includes(bugFilter));
  }, [data, bugFilter]);

  const tokenTotals = useMemo(() => {
    const tokens = filteredEntries.reduce((s, e) => s + (e.tokenUsage?.total ?? 0), 0);
    const cost   = filteredEntries.reduce((s, e) => s + (e.cost_usd ?? 0), 0);
    return { tokens, cost };
  }, [filteredEntries]);

  // Early returns — auth guard
  if (loading) return null;
  if (teamUser?.trustLevel !== 'sysadmin') {
    return <Navigate to="/" replace />;
  }

  const staleLabel = formatAgo(data?.updatedAt);

  return (
    <div className={styles.wrap}>
      {/* Filter bar */}
      <div className={styles.filterBar}>
        <div className={styles.filterBarLeft}>
          <div className={styles.segmented} role="tablist" aria-label="Rango de tiempo">
            {RANGES.map(r => (
              <button
                key={r.key}
                role="tab"
                aria-selected={range === r.key}
                className={`${styles.segBtn} ${range === r.key ? styles.segActive : ''}`}
                onClick={() => setRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className={styles.filterBarRight}>
          <span className={styles.updatedAt} title={data?.updatedAt ?? ''}>
            Actualizado {staleLabel}
          </span>
          <button
            className={styles.refreshBtn}
            onClick={() => setRefreshTick(t => t + 1)}
            disabled={fetching}
            title="Refrescar ahora"
            aria-label="Refrescar ahora"
          >
            <IconRefresh spinning={fetching} />
          </button>
          <label className={styles.autoToggle} title={`Auto-refresh cada ${AUTO_REFRESH_MS / 1000}s`}>
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            <span>AUTO</span>
          </label>
        </div>
      </div>

      {error && (
        <div className={styles.errorState}>Error al cargar auditoría: {error}</div>
      )}

      {fetching && !data && (
        <div className={styles.loadingState}>Cargando métricas...</div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className={styles.kpiGrid}>
            <Kpi label="Total PRs"    value={String(data.summary.total)} />
            <Kpi
              label="Clean merge %"
              value={
                data.summary.total > 0
                  ? `${(((data.summary.outcome.clean_merge ?? 0) / data.summary.total) * 100).toFixed(0)}%`
                  : '—'
              }
            />
            <Kpi
              label="Avg iterations"
              value={data.summary.avg_iterations != null ? data.summary.avg_iterations.toFixed(2) : '—'}
            />
            <Kpi label="Mostrando" value={`${filteredEntries.length}/${data.total}`} />
            <Kpi
              label="Total tokens"
              value={tokenTotals.tokens > 0 ? tokenTotals.tokens.toLocaleString('es-CL') : '—'}
            />
            <Kpi
              label="Costo total"
              value={tokenTotals.cost > 0 ? `$${tokenTotals.cost.toFixed(2)}` : '—'}
            />
          </div>

          {/* Outcome + Donut */}
          <div className={styles.row2}>
            <section className={styles.card}>
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Outcome distribution</h2>
                <span className={styles.cardSub}>{data.summary.total} prs</span>
              </header>
              <OutcomeStacked data={data.summary.outcome} />
            </section>
            <section className={styles.card}>
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Clean merge ratio</h2>
              </header>
              <CleanMergeDonut
                outcome={data.summary.outcome}
                avgIterations={data.summary.avg_iterations}
              />
            </section>
          </div>

          {/* Trend sparkline */}
          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Clean merge trend</h2>
              <span className={styles.cardSub}>
                {range === '7d' ? 'por día' : 'por semana'}
              </span>
            </header>
            <TrendSparkline entries={data.entries} range={range} />
          </section>

          {/* Repo breakdown */}
          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Por repositorio</h2>
              <span className={styles.cardSub}>outcome · clean% · total</span>
            </header>
            <RepoBreakdown entries={data.entries} />
          </section>

          {/* Bug classes */}
          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Bug classes</h2>
              <span className={styles.cardSub}>Click para filtrar tabla</span>
            </header>
            <BarList<AuditBugClass>
              data={data.summary.bug_classes}
              getLabel={(k) => BUG_CLASS_LABEL[k] ?? k}
              getColor={() => 'var(--accent)'}
              activeKey={bugFilter}
              onToggle={(k) => setBugFilter(curr => curr === k ? null : k)}
            />
          </section>

          {/* Responsible elements */}
          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Responsible elements</h2>
              <span className={styles.cardSub}>PRs atribuidos (multi)</span>
            </header>
            <BarList<AuditResponsibleElement>
              data={data.summary.responsible_elements}
              order={RESPONSIBLE_ORDER}
              getLabel={(k) => RESPONSIBLE_META[k]?.label ?? k}
              getColor={(k) => RESPONSIBLE_META[k]?.color ?? 'var(--muted)'}
            />
          </section>

          {/* Bug × responsible heatmap */}
          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Bug class × Responsable</h2>
              <span className={styles.cardSub}>intensidad = coincidencias</span>
            </header>
            <BugHeatmap entries={data.entries} />
          </section>

          {/* Active bug filter banner */}
          {bugFilter && (
            <div>
              <span className={styles.filterBanner}>
                Filtrando por <strong>{BUG_CLASS_LABEL[bugFilter]}</strong>
                <button className={styles.clearFilter} onClick={() => setBugFilter(null)}>
                  quitar
                </button>
              </span>
            </div>
          )}

          {/* Table */}
          <section className={styles.card}>
            <header className={styles.cardHeader}>
              <h2 className={styles.cardTitle}>Últimos PRs auditados</h2>
              <span className={styles.cardSub}>
                {filteredEntries.length} entries · click para expandir
              </span>
            </header>
            {filteredEntries.length === 0 ? (
              <div className={styles.emptyState}>No hay PRs para este filtro.</div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th aria-hidden="true" style={{ width: 20 }} />
                      <th>Ticket</th>
                      <th>Repo</th>
                      <th>PR</th>
                      <th>Outcome</th>
                      <th>Iters</th>
                      <th>Tokens</th>
                      <th>USD $</th>
                      <th>Bug classes</th>
                      <th>Responsable</th>
                      <th>Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEntries.map(entry => {
                      const key = `${entry.pr_url}-${entry.ticket_id}`;
                      return (
                        <PrRow
                          key={key}
                          entry={entry}
                          expanded={expandedKey === key}
                          onToggleExpand={() => setExpandedKey(k => k === key ? null : key)}
                          onBugClick={(k) => setBugFilter(curr => curr === k ? null : k)}
                          activeBug={bugFilter}
                        />
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Parse errors (only if any) */}
          {data.parseErrors.length > 0 && (
            <section className={styles.card}>
              <header className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Parse errors</h2>
                <span className={styles.cardSub}>{data.parseErrors.length}</span>
              </header>
              <div className={styles.barList}>
                {data.parseErrors.map((e, i) => (
                  <div key={i} className={styles.barRow} style={{ gridTemplateColumns: '80px 1fr' }}>
                    <span className={styles.barLabel}>Line {e.line}</span>
                    <span className={styles.barLabel}>{e.error}</span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}

// ─── Kpi tile ──────────────────────────────────────────────────────────────

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.kpi}>
      <span className={styles.kpiLabel}>{label}</span>
      <span className={`${styles.kpiValue} ${styles.mono}`}>{value}</span>
    </div>
  );
}

// ─── Row ───────────────────────────────────────────────────────────────────

function PrRow({
  entry, onBugClick, activeBug, expanded, onToggleExpand,
}: {
  entry: AuditEntry;
  onBugClick: (k: AuditBugClass) => void;
  activeBug: AuditBugClass | null;
  expanded: boolean;
  onToggleExpand: () => void;
}) {
  const meta = OUTCOME_META[entry.outcome] ?? { label: entry.outcome, color: 'var(--muted)' };
  const hasEvidence = Boolean(
    entry.evidence?.files?.length
    || entry.evidence?.quoted_diff
    || entry.proposed_general_rules?.length
    || entry.notes
  );

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    // Ignora clicks en chips (bug filter), en links (PR), o en el toggle
    const target = e.target as HTMLElement;
    if (target.closest(`.${styles.chip}`) || target.closest(`.${styles.prLink}`)) return;
    onToggleExpand();
  };

  return (
    <>
      <tr
        className={`${styles.rowClickable} ${expanded ? styles.rowExpanded : ''}`}
        onClick={handleRowClick}
        aria-expanded={hasEvidence ? expanded : undefined}
      >
        <td className={styles.expandCell}>
          <span className={`${styles.expandIcon} ${expanded ? styles.expandIconOpen : ''}`} aria-hidden="true">
            ▸
          </span>
        </td>
        <td className={styles.tdStrong}>{entry.ticket_id}</td>
        <td className={styles.tdMono}>{entry.repo}</td>
        <td>
          <a className={styles.prLink} href={entry.pr_url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>
            #{entry.pr_number}
          </a>
        </td>
        <td>
          <span
            className={styles.outcomeChip}
            style={{
              color: meta.color,
              background: `color-mix(in srgb, ${meta.color} 14%, transparent)`,
            }}
          >
            <span className={styles.outcomeDot} style={{ background: meta.color }} />
            {meta.label}
          </span>
        </td>
        <td className={styles.tdMono}>{entry.iterations}</td>
        <td className={styles.tdMono}>
          {entry.tokenUsage?.total != null && entry.tokenUsage.total > 0
            ? entry.tokenUsage.total.toLocaleString('es-CL')
            : '—'}
        </td>
        <td className={styles.tdMono}>
          {entry.cost_usd != null && entry.cost_usd > 0
            ? `$${entry.cost_usd.toFixed(3)}`
            : '—'}
        </td>
        <td>
          <div className={styles.chipRow}>
            {entry.bug_classes.length === 0 && <span className={styles.chip} style={{ cursor: 'default' }}>—</span>}
            {entry.bug_classes.map(k => (
              <button
                key={k}
                type="button"
                className={`${styles.chip} ${activeBug === k ? styles.chipActive : ''}`}
                onClick={(e) => { e.stopPropagation(); onBugClick(k); }}
                aria-pressed={activeBug === k}
              >
                {BUG_CLASS_LABEL[k] ?? k}
              </button>
            ))}
          </div>
        </td>
        <td>
          <div className={styles.chipRow}>
            {entry.responsible_elements.map(k => {
              const rMeta = RESPONSIBLE_META[k];
              const color = rMeta?.color ?? 'var(--muted)';
              return (
                <span
                  key={k}
                  className={styles.chip}
                  style={{
                    color,
                    borderColor: `color-mix(in srgb, ${color} 40%, var(--border))`,
                    cursor: 'default',
                  }}
                >
                  {rMeta?.label ?? k}
                </span>
              );
            })}
          </div>
        </td>
        <td className={styles.tdMono}>{formatDate(entry.timestamp)}</td>
      </tr>
      {expanded && (
        <tr className={styles.expandRow}>
          <td colSpan={11}>
            {!hasEvidence ? (
              <div className={styles.emptyEvidence}>Sin evidence, proposed rules ni notes.</div>
            ) : (
              <div className={styles.evidenceGrid}>
                {entry.evidence?.files && entry.evidence.files.length > 0 && (
                  <div className={styles.evidenceBlock}>
                    <div className={styles.evidenceLabel}>Files ({entry.evidence.files.length})</div>
                    <div className={styles.chipRow}>
                      {entry.evidence.files.map((f, i) => (
                        <span key={`${f}-${i}`} className={`${styles.chip} ${styles.fileChip}`} style={{ cursor: 'default' }} title={f}>
                          {f}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {entry.evidence?.quoted_diff && (
                  <div className={styles.evidenceBlock}>
                    <div className={styles.evidenceLabel}>Quoted diff</div>
                    <pre className={styles.quotedDiff}>{entry.evidence.quoted_diff}</pre>
                  </div>
                )}
                {entry.proposed_general_rules && entry.proposed_general_rules.length > 0 && (
                  <div className={styles.evidenceBlock}>
                    <div className={styles.evidenceLabel}>Proposed general rules</div>
                    <ul className={styles.rulesList}>
                      {entry.proposed_general_rules.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
                {entry.notes && (
                  <div className={styles.evidenceBlock}>
                    <div className={styles.evidenceLabel}>Notes</div>
                    <div className={styles.notesBox}>{entry.notes}</div>
                  </div>
                )}
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function IconRefresh({ spinning }: { spinning?: boolean }) {
  return (
    <svg
      width="14" height="14" viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      className={spinning ? styles.spinning : ''}
      aria-hidden="true"
    >
      <path d="M13.5 8A5.5 5.5 0 112.5 5M2.5 2v3h3" />
    </svg>
  );
}
